import type { AdminCardStatus, ListAdminCardsResultDto } from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin payout-cards surface (AUT-126 /
 * ADMIN-14), targeting the AUT-115 backend:
 *
 *   - `GET  /admin/cards`                   (cursor-paginated, cross-inspector)
 *   - `POST /admin/cards/:id/force-unbind`  (empty body)
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list → `['admin', 'cards', 'list', params]`
 * The force-unbind mutation invalidates by the `['admin', 'cards']` prefix
 * on success (and on ANY rejection — re-syncs the action-eligibility gate).
 *
 * Mirrors `admin-payouts.ts`, with deliberate divergences (architect
 * guardrails — NON-NEGOTIABLE):
 *
 * INVALIDATE-ONLY mutation. Force-unbind returns the `{dto, previousStatus}`
 * envelope (`dto: AdminCardDto`) — the SPA does NOT cross-patch the cache
 * from the response; it invalidates the prefix and lets TanStack refetch.
 * Refetch is the source of truth for the status-gated action eligibility.
 *
 * NO detail endpoint — the cards surface is a flat monitoring list (and the
 * inspector-detail Cards tab filters the SAME list by `inspectorId`); there
 * is no per-card drawer to key a detail query against.
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const CARDS_BASE = '/admin/cards';

/** Stable query-key prefix; the force-unbind mutation invalidates against this. */
export const ADMIN_CARDS_KEY = ['admin', 'cards'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminCardsQuerySchema`:
 *   - `status` — multi-select over the FULL 7-value `ADMIN_CARD_STATUSES`
 *     (CSV on the wire);
 *   - `inspectorId` — exact-match UUID (also how the inspector-detail Cards
 *     tab scopes the list to one inspector);
 *   - `cursor` / `limit` — cursor pagination over `createdAt DESC, id DESC`.
 * NO `q` free-text and NO date filter — neither is in the query schema.
 */
export interface AdminCardsListParams {
  status?: AdminCardStatus[];
  inspectorId?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminCardsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.inspectorId && params.inspectorId.length > 0) query.inspectorId = params.inspectorId;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

/**
 * List query. `enabled` defaults to `true`; the inspector-detail Cards tab
 * passes `enabled: false` while the tab is inactive so the cross-inspector
 * list never fetches until the operator opens that tab.
 */
export const useAdminCardsList = (
  params: AdminCardsListParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<ListAdminCardsResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_CARDS_KEY, 'list', params],
    enabled: options.enabled ?? true,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminCardsResultDto>(CARDS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

/**
 * Low-level force-unbind mutation. Empty request body — the action itself
 * is the forensic signal. The backend revokes the WebPay binding and flips
 * the row to `unbound` (no-op for non-`active`/null-token cards). Role +
 * permission + audit gated server-side (AUT-138 removed per-action TOTP
 * step-up).
 *
 * Returns nothing useful to the SPA — the response is the
 * `{dto, previousStatus}` envelope; the caller invalidates rather than
 * cross-patching. A 409 `card_has_pending_payouts` is mapped to a
 * card-specific toast in `useForceUnbind` (the shared interceptor's generic
 * 409 toast is suppressed there via a stable toast id).
 */
export const postForceUnbindCard = async (id: string): Promise<void> => {
  await apiClient.post<unknown>(`${CARDS_BASE}/${id}/force-unbind`, {});
};

/**
 * Cache-helper for the force-unbind hook. ONLY exposes `invalidate` (no
 * `setQueryData`): the mutation returns the `{dto, previousStatus}` envelope,
 * and refetch via invalidation is the source of truth — it re-syncs the
 * status-gated action eligibility after success AND after any rejection.
 */
export const useAdminCardsCache = (): {
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_CARDS_KEY }),
  };
};
